/**
 * @module components/auth/AppNavbar
 * @summary Top navigation bar shown after login (brand, tabs, user badge).
 * @description Tabs switch the active panel. Role visibility matches the
 *   original: engineers see only "Tool"; managers also see "Symbols";
 *   super-admins additionally see "Admin". "Materials" is reference data that
 *   the tool only reads, so every role gets it.
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 391-407 + applyRoleVisibility (4581-4586).
 */
import { useAuthStore } from '../../state/authStore';
import { ROLE_LABELS } from '../../lib/auth';

export type Panel = 'tool' | 'formulation' | 'calcs' | 'sim' | 'materials' | 'symbols' | 'users';

interface Props {
  active: Panel;
  onNavigate: (p: Panel) => void;
}

export function AppNavbar({ active, onNavigate }: Props) {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  if (!user) return null;

  const showSymbols = user.role !== 'engineer';
  const showAdmin = user.role === 'superadmin';

  return (
    <div id="app-navbar" style={{ display: 'flex' }}>
      <div id="nav-brand">
        <div id="nav-brand-title">JCC PFD</div>
        <div id="nav-brand-sub">Engineering Tool</div>
      </div>
      <div id="nav-links">
        <button className={'nav-link' + (active === 'tool' ? ' active' : '')} onClick={() => onNavigate('tool')}>
          🗂 Tool
        </button>
        <button
          className={'nav-link' + (active === 'formulation' ? ' active' : '')}
          onClick={() => onNavigate('formulation')}
        >
          ⚗ Formulation
        </button>
        <button
          className={'nav-link' + (active === 'calcs' ? ' active' : '')}
          onClick={() => onNavigate('calcs')}
        >
          🧮 Line Calcs
        </button>
        <button className={'nav-link' + (active === 'sim' ? ' active' : '')} onClick={() => onNavigate('sim')}>
          ⚙ Simulation
        </button>
        <button
          className={'nav-link' + (active === 'materials' ? ' active' : '')}
          onClick={() => onNavigate('materials')}
        >
          🧪 Materials
        </button>
        {showSymbols && (
          <button className={'nav-link' + (active === 'symbols' ? ' active' : '')} onClick={() => onNavigate('symbols')}>
            ⊕ Symbols
          </button>
        )}
        {showAdmin && (
          <button className={'nav-link' + (active === 'users' ? ' active' : '')} onClick={() => onNavigate('users')}>
            ⚙ Admin
          </button>
        )}
      </div>
      <div id="nav-spacer" />
      <div id="nav-right">
        <span id="tb-user-badge">
          {user.username}
          <span className={'tb-role-pill ' + user.role}>{ROLE_LABELS[user.role]}</span>
        </span>
        <button
          id="btn-logout"
          title="Sign out"
          onClick={() => {
            if (confirm('Sign out of the PFD tool?')) logout();
          }}
        >
          ⎋ Logout
        </button>
      </div>
    </div>
  );
}
